import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Users,
  AlarmClock,
  SlidersHorizontal,
  Zap,
  ShieldCheck,
  Check,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { DownloadButton } from '@/components/marketing/DownloadButton';
import { RouteHead } from '@/components/seo/RouteHead';
import { ROUTES } from '@/routes';
import { fadeUp, fadeIn, staggerContainer, VIEWPORT_REVEAL, STAGGER } from '@/lib/motion';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface FeatureSectionProps {
  icon: LucideIcon;
  badge: string;
  title: string;
  description: string;
  points: string[];
  reverse?: boolean;
}

function FeatureSection({ icon: Icon, badge, title, description, points, reverse }: FeatureSectionProps) {
  return (
    <motion.div
      variants={staggerContainer(STAGGER.base)}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_REVEAL}
      className={[
        'grid items-center gap-10 lg:grid-cols-2',
        reverse ? 'lg:[&>*:first-child]:order-2' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <motion.div variants={fadeIn} className="flex justify-center">
        <div className="flex h-48 w-48 items-center justify-center rounded-3xl border border-border bg-card shadow-elevated">
          <Icon className="h-20 w-20 text-brand" aria-hidden="true" />
        </div>
      </motion.div>
      <motion.div variants={staggerContainer(STAGGER.base)} className="flex flex-col gap-5">
        <motion.span
          variants={fadeUp}
          className="inline-block w-fit rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand"
        >
          {badge}
        </motion.span>
        <motion.div variants={fadeUp}>
          <Heading level={3}>{title}</Heading>
        </motion.div>
        <motion.p variants={fadeUp} className="text-muted-foreground">
          {description}
        </motion.p>
        <motion.ul variants={staggerContainer(STAGGER.tight)} className="flex flex-col gap-3">
          {points.map((point) => (
            <motion.li key={point} variants={fadeUp} className="flex items-start gap-3 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
              <span>{point}</span>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </motion.div>
  );
}

export default function FeaturesPage() {
  const { t } = useTranslation('features');

  const features: FeatureSectionProps[] = [
    {
      icon: Bell,
      badge: t('fullscreen_alerts.badge'),
      title: t('fullscreen_alerts.title'),
      description: t('fullscreen_alerts.description'),
      points: [
        t('fullscreen_alerts.point_1'),
        t('fullscreen_alerts.point_2'),
        t('fullscreen_alerts.point_3'),
      ],
    },
    {
      icon: Users,
      badge: t('multi_account.badge'),
      title: t('multi_account.title'),
      description: t('multi_account.description'),
      points: [
        t('multi_account.point_1'),
        t('multi_account.point_2'),
        t('multi_account.point_3'),
      ],
      reverse: true,
    },
    {
      icon: AlarmClock,
      badge: t('snooze.badge'),
      title: t('snooze.title'),
      description: t('snooze.description'),
      points: [t('snooze.point_1'), t('snooze.point_2'), t('snooze.point_3')],
    },
    {
      icon: SlidersHorizontal,
      badge: t('customization.badge'),
      title: t('customization.title'),
      description: t('customization.description'),
      points: [
        t('customization.point_1'),
        t('customization.point_2'),
        t('customization.point_3'),
      ],
      reverse: true,
    },
    {
      icon: Zap,
      badge: t('low_latency.badge'),
      title: t('low_latency.title'),
      description: t('low_latency.description'),
      points: [t('low_latency.point_1'), t('low_latency.point_2'), t('low_latency.point_3')],
    },
    {
      icon: ShieldCheck,
      badge: t('privacy.badge'),
      title: t('privacy.title'),
      description: t('privacy.description'),
      points: [t('privacy.point_1'), t('privacy.point_2'), t('privacy.point_3')],
      reverse: true,
    },
  ];

  return (
    <>
      <RouteHead
        path={ROUTES.features}
        titleKey="seo:features.title"
        descriptionKey="seo:features.description"
      />
      {/* Hero */}
      <Section padding="lg">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fadeIn}
              className="mb-4 text-xs font-semibold uppercase tracking-widest text-brand"
            >
              {t('page.title')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t('hero.headline')}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              {t('hero.subheadline')}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* Feature sections */}
      <Section padding="lg" bordered>
        <Container size="xl" className="flex flex-col gap-24">
          {features.map((feature) => (
            <FeatureSection key={feature.title} {...feature} />
          ))}
        </Container>
      </Section>

      {/* CTA */}
      <Section padding="lg" className="bg-primary-soft">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('cta.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-md text-muted-foreground">
              {t('cta.subheadline')}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex justify-center">
              <DownloadButton />
            </motion.div>
          </motion.div>
        </Container>
      </Section>
    </>
  );
}
